import { FormField as Field } from "@/components/shared/FormField";
import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Loader2, FileText, Lock, Eye, EyeOff,
  User, Mail, Phone, MapPin, Save, Trash2, AlertTriangle, X,
  CheckSquare, ExternalLink, Baby, Plus, Pencil,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { INCOME_SOURCES, DEDUCTION_SOURCES } from "./fiscal/catalogs";
import InstalmentsSection from "./fiscal/InstalmentsSection";
import { API, authH } from "@/api";

const maskNas = (nas) => {
  if (!nas) return "—";
  const digits = nas.replace(/[^0-9]/g, "");
  if (digits.length < 4) return nas;
  return `••• ••• ${digits.slice(-3)}`;
};

/**
 * Étape 4 — Page indépendante de la fiche Master.
 *  HAUT : bandeau Master en lecture seule (synchronisé via GET /masters/{id})
 *  BAS  : champs fiscaux éditables (collection fiscal_dossiers)
 */
export default function FiscalDossierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [master, setMaster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [showArchive, setShowArchive] = useState(false);
  const [revealPrimary, setRevealPrimary] = useState(false);
  const [revealSecondary, setRevealSecondary] = useState(false);

  // Dirty tracking
  const dirty = form && dossier ? Object.keys(form).some((k) => {
    const a = JSON.stringify(form[k] ?? null);
    const b = JSON.stringify(dossier[k] ?? null);
    return a !== b;
  }) : false;
  const confirmLeave = useUnsavedChanges(dirty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/fiscal-dossiers/${id}`, { headers: authH() });
      setDossier(r.data);
      setForm({
        sin_primary: r.data.sin_primary || "",
        sin_secondary: r.data.sin_secondary || "",
        marital_status: r.data.marital_status || "",
        income_sources: r.data.income_sources || [],
        deduction_sources: r.data.deduction_sources || [],
        last_declaration_filed: !!r.data.last_declaration_filed,
        current_declaration_in_progress: !!r.data.current_declaration_in_progress,
        notes: r.data.notes || "",
      });
      // Charge le master en parallèle (read-only)
      try {
        const m = await axios.get(`${API}/masters/${r.data.master_id}`, { headers: authH() });
        setMaster(m.data);
      } catch {
        setMaster(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur chargement");
      navigate("/admin/fiscal-dossiers");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Batch #14.122 — Breadcrumb : Accueil › Fiscal › Dossiers Fiscaux › Master <couple> › Dossier Fiscal <year>
  const { setTrail } = useBreadcrumb();
  useEffect(() => {
    if (!dossier) return undefined;
    const couple = master?.couple_display_name || "(Master inconnu)";
    setTrail([
      { label: "Fiscal", href: "/admin/fiscal" },
      { label: "Dossiers Fiscaux", href: "/admin/fiscal-dossiers" },
      { label: couple, href: `/admin/masters/${dossier.master_id}` },
      { label: `Dossier Fiscal · ${dossier.tax_year}` },
    ]);
    return () => setTrail(null);
  }, [dossier, master, setTrail]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleArr = (key, value) => {
    setForm((f) => {
      const arr = new Set(f[key] || []);
      if (arr.has(value)) arr.delete(value); else arr.add(value);
      return { ...f, [key]: Array.from(arr) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        sin_primary: form.sin_primary || null,
        sin_secondary: form.sin_secondary || null,
        marital_status: form.marital_status || null,
        notes: form.notes || null,
      };
      const r = await axios.put(`${API}/fiscal-dossiers/${id}`, payload, { headers: authH() });
      setDossier(r.data);
      toast.success("Dossier fiscal sauvegardé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    try {
      await axios.delete(`${API}/fiscal-dossiers/${id}`, { headers: authH() });
      toast.success("Dossier fiscal archivé");
      navigate("/admin/fiscal-dossiers");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const auditReveal = async (which) => {
    try {
      await axios.post(`${API}/fiscal-dossiers/${id}/audit-nas-reveal`, { which }, { headers: authH() });
    } catch { /* audit best-effort */ }
  };

  if (loading) {
    return <div className="text-center py-20"><Loader2 className="inline w-7 h-7 animate-spin text-slate-400" /></div>;
  }
  if (!dossier || !form) return null;

  return (
    <div className="space-y-5" data-testid="fiscal-dossier-detail">
      {/* Header navigation + archive */}
      <div className="flex items-center justify-between">
        <Link
          to="/admin/fiscal-dossiers"
          onClick={(e) => { if (!confirmLeave()) e.preventDefault(); }}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          data-testid="fiscal-detail-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Retour aux dossiers fiscaux
        </Link>
        <button
          onClick={() => setShowArchive(true)}
          className="text-xs px-2.5 py-1 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1"
          data-testid="fiscal-detail-archive-btn"
        >
          <Trash2 className="w-3 h-3" /> Archiver ce dossier
        </button>
      </div>

      {/* Titre */}
      <div className="bg-gradient-to-br from-blue-50 to-white border border-slate-200 rounded-2xl p-5" data-testid="fiscal-detail-header">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                Dossier Fiscal · {dossier.tax_year}
              </h1>
              <div className="text-sm text-slate-600">
                {master?.couple_display_name || "(Master inconnu)"}
              </div>
              <div className="text-[11px] text-slate-400 font-mono mt-0.5">{dossier.id}</div>
            </div>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-2">
          Créé le {new Date(dossier.created_at).toLocaleDateString("fr-CA")} par {dossier.created_by || "—"}
        </div>
      </div>

      {/* Section MASTER (lecture seule) */}
      <MasterReadOnlySection master={master} masterId={dossier.master_id} />

      {/* Batch #14.329 — Acomptes provisionnels du particulier */}
      <InstalmentsSection dossierId={dossier.id} masterId={dossier.master_id} clientName={master?.couple_display_name || ""} />

      {/* Section FISCAL (éditable) */}
      <div className="border border-slate-200 rounded-xl p-5 bg-white" data-testid="fiscal-detail-form">
        <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <CheckSquare className="w-5 h-5 text-blue-600" /> Champs fiscaux (propres à ce dossier)
        </h2>

        {/* État civil */}
        <Field label="État civil" hint="Champ libre — l'utilisateur écrit ce qu'il veut (100 chars max)">
          <input
            type="text"
            value={form.marital_status}
            onChange={(e) => upd("marital_status", e.target.value)}
            maxLength={100}
            placeholder="ex: union de fait, marié, célibataire, veuf, divorcé…"
            className="cc-input"
            data-testid="fiscal-marital-status"
          />
        </Field>

        {/* NAS — masqués avec révélation */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <NasField
            label={`NAS ${master?.primary_user ? `${master.primary_user.first_name} ${master.primary_user.last_name}` : "Principal"}`}
            value={form.sin_primary}
            onChange={(v) => upd("sin_primary", v)}
            revealed={revealPrimary}
            onToggleReveal={() => {
              const next = !revealPrimary;
              setRevealPrimary(next);
              if (next) auditReveal("sin_primary");
            }}
            testid="fiscal-sin-primary"
          />
          {master?.secondary_user && (
            <NasField
              label={`NAS ${master.secondary_user.first_name} ${master.secondary_user.last_name}`}
              value={form.sin_secondary}
              onChange={(v) => upd("sin_secondary", v)}
              revealed={revealSecondary}
              onToggleReveal={() => {
                const next = !revealSecondary;
                setRevealSecondary(next);
                if (next) auditReveal("sin_secondary");
              }}
              testid="fiscal-sin-secondary"
            />
          )}
        </div>

        {/* Sources de revenu */}
        <CheckboxGrid
          title="Sources de revenu"
          items={INCOME_SOURCES}
          selected={form.income_sources}
          onToggle={(id) => toggleArr("income_sources", id)}
          testidPrefix="fiscal-income"
        />

        {/* Sources de déduction */}
        <CheckboxGrid
          title="Sources de déduction"
          items={DEDUCTION_SOURCES}
          selected={form.deduction_sources}
          onToggle={(id) => toggleArr("deduction_sources", id)}
          testidPrefix="fiscal-deduction"
        />

        {/* Statut déclarations */}
        <div className="mt-5">
          <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">
            Statut des déclarations
          </h3>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.last_declaration_filed}
                onChange={(e) => upd("last_declaration_filed", e.target.checked)}
                data-testid="fiscal-last-declaration-filed"
              />
              <span className="text-sm">Dernière déclaration remise</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.current_declaration_in_progress}
                onChange={(e) => upd("current_declaration_in_progress", e.target.checked)}
                data-testid="fiscal-current-declaration-in-progress"
              />
              <span className="text-sm">Déclaration en cours</span>
            </label>
          </div>
        </div>

        {/* Étape 5 — Enfants (CRUD individuel embarqué) */}
        <ChildrenSection
          dossierId={id}
          children={dossier.children || []}
          onChange={(updatedDossier) => setDossier(updatedDossier)}
        />

        {/* Notes */}
        <Field label="Notes internes" hint="Visibles uniquement par l'admin (1000 chars max)" className="mt-5">
          <textarea
            rows={3}
            value={form.notes}
            onChange={(e) => upd("notes", e.target.value)}
            maxLength={1000}
            className="cc-input"
            data-testid="fiscal-notes"
          />
        </Field>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 mt-5 border-t border-slate-100">
          <span className="text-[11px] text-slate-500">
            {dirty ? "⚠️ Modifications non sauvegardées" : "Tout est à jour"}
          </span>
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={saving || !dirty}
              className="px-4 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="fiscal-detail-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Enregistrer
            </button>
          </div>
        </div>
      </div>

      {showArchive && (
        <ConfirmArchive
          onClose={() => setShowArchive(false)}
          onConfirm={archive}
        />
      )}

      <style>{`
        .cc-input { width: 100%; padding: 0.55rem 0.75rem; border: 1px solid #e2e8f0; border-radius: 0.5rem; font-size: 0.875rem; outline: none; }
        .cc-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 1px #3b82f6; }
      `}</style>
    </div>
  );
}

// ============================================================
//  Section Master en lecture seule
// ============================================================
function MasterReadOnlySection({ master, masterId }) {
  if (!master) {
    return (
      <div className="border border-amber-200 bg-amber-50 rounded-xl p-4 text-sm text-amber-800">
        Master {masterId.slice(0, 8)}… introuvable (peut-être archivé). Le dossier fiscal reste consultable.
      </div>
    );
  }

  const cc = master.common_contact || {};
  const fullAddr = [
    [cc.civic_number, cc.street].filter(Boolean).join(" "),
    cc.apartment ? `App. ${cc.apartment}` : null,
    cc.city, cc.province, cc.postal_code,
  ].filter(Boolean).join(", ");

  return (
    <div className="border border-slate-200 bg-slate-50/60 rounded-xl p-5" data-testid="fiscal-master-readonly">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-800 flex items-center gap-2">
          <Lock className="w-4 h-4 text-slate-500" /> Informations Master (synchronisées, lecture seule)
        </h2>
        <Link
          to={`/admin/masters/${master.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
          data-testid="fiscal-master-edit-link"
        >
          Modifier la fiche Master <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <MemberCard user={master.primary_user} role="Principal" tone="indigo" />
        {master.secondary_user ? (
          <MemberCard user={master.secondary_user} role="Secondaire" tone="purple" />
        ) : (
          <div className="border border-dashed border-slate-300 rounded-lg p-4 text-center text-xs text-slate-400">
            Aucun conjoint·e lié
          </div>
        )}
      </div>

      <div className="border-t border-slate-200 pt-3 text-sm">
        {fullAddr ? (
          <div className="inline-flex items-start gap-2 text-slate-700">
            <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <span data-testid="fiscal-master-address">{fullAddr}</span>
          </div>
        ) : (
          <div className="text-slate-400 italic text-xs">Aucune adresse renseignée dans le Master.</div>
        )}
        {cc.home_phone && (
          <div className="inline-flex items-center gap-2 text-slate-600 ml-4">
            <Phone className="w-4 h-4 text-slate-400" /> {cc.home_phone} (maison)
          </div>
        )}
      </div>
    </div>
  );
}

function MemberCard({ user, role, tone }) {
  if (!user) return null;
  const tones = {
    indigo: "bg-indigo-100 text-indigo-700",
    purple: "bg-purple-100 text-purple-700",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`fiscal-member-${role.toLowerCase()}`}>
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-600 flex-shrink-0">
          {(user.first_name?.[0] || "").toUpperCase()}{(user.last_name?.[0] || "").toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-800 text-sm truncate">{user.first_name} {user.last_name}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tones[tone] || tones.indigo}`}>{role}</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 space-y-0.5">
            {user.date_naissance && <div className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {user.date_naissance}</div>}
            {user.email && <div className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {user.email}</div>}
            {user.phone && <div className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {user.phone}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
//  Champ NAS avec révélation auditée
// ============================================================
function NasField({ label, value, onChange, revealed, onToggleReveal, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-semibold text-slate-600">{label}</label>
        <button
          onClick={onToggleReveal}
          type="button"
          className="text-[10px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
          data-testid={`${testid}-toggle`}
        >
          {revealed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          {revealed ? "Masquer" : "Révéler"}
        </button>
      </div>
      <input
        type={revealed ? "text" : "password"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        maxLength={20}
        placeholder={revealed ? "XXX-XXX-XXX" : maskNas(value)}
        className="cc-input font-mono"
        data-testid={testid}
      />
      {!revealed && value && (
        <div className="text-[10px] text-slate-400 mt-0.5">Affiché masqué : {maskNas(value)}</div>
      )}
    </div>
  );
}

// ============================================================
//  Grille de checkboxes (sources revenus / déductions)
// ============================================================
function CheckboxGrid({ title, items, selected, onToggle, testidPrefix }) {
  const selectedSet = new Set(selected || []);
  return (
    <div className="mt-5">
      <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">
        {title} <span className="text-slate-400 font-normal">({selectedSet.size} sélectionnée(s))</span>
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.map((it) => (
          <label
            key={it.id}
            className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition ${
              selectedSet.has(it.id)
                ? "bg-blue-50 border-blue-300 text-blue-900"
                : "bg-white border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`${testidPrefix}-${it.id}`}
          >
            <input
              type="checkbox"
              checked={selectedSet.has(it.id)}
              onChange={() => onToggle(it.id)}
              className="text-blue-600"
            />
            <span className="text-sm">{it.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ============================================================
//  Étape 5 — Section Enfants (CRUD individuel)
// ============================================================
function ChildrenSection({ dossierId, children, onChange }) {
  const [editing, setEditing] = useState(null); // null | "new" | child object
  const [busy, setBusy] = useState(false);

  const addChild = async (data) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/fiscal-dossiers/${dossierId}/children`, data, { headers: authH() });
      onChange(r.data);
      toast.success(`${data.first_name} ajouté(e)`);
      setEditing(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const updateChild = async (childId, data) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/fiscal-dossiers/${dossierId}/children/${childId}`, data, { headers: authH() });
      onChange(r.data);
      toast.success("Enfant mis à jour");
      setEditing(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const deleteChild = async (child) => {
    if (!window.confirm(`Supprimer ${child.first_name} ${child.last_name} ?`)) return;
    setBusy(true);
    try {
      const r = await axios.delete(`${API}/fiscal-dossiers/${dossierId}/children/${child.id}`, { headers: authH() });
      onChange(r.data);
      toast.success(`${child.first_name} supprimé(e)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 border-t border-slate-100 pt-5" data-testid="fiscal-children-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
          <Baby className="w-4 h-4" />
          Enfants <span className="text-slate-400 font-normal">({children.length})</span>
        </h3>
        <button
          onClick={() => setEditing("new")}
          className="text-xs px-2.5 py-1 rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 inline-flex items-center gap-1"
          data-testid="fiscal-children-add-btn"
        >
          <Plus className="w-3 h-3" /> Ajouter un enfant
        </button>
      </div>

      {children.length === 0 ? (
        <div className="text-center py-6 border border-dashed border-slate-200 rounded-lg text-xs text-slate-400" data-testid="fiscal-children-empty">
          Aucun enfant lié à ce dossier fiscal.
        </div>
      ) : (
        <div className="space-y-2">
          {children.map((c) => (
            <ChildCard
              key={c.id}
              child={c}
              dossierId={dossierId}
              onEdit={() => setEditing(c)}
              onDelete={() => deleteChild(c)}
              busy={busy}
            />
          ))}
        </div>
      )}

      {editing && (
        <ChildEditorModal
          child={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={(data) => editing === "new" ? addChild(data) : updateChild(editing.id, data)}
          busy={busy}
        />
      )}
    </div>
  );
}

function ChildCard({ child, dossierId, onEdit, onDelete, busy }) {
  const [revealed, setRevealed] = useState(false);

  const auditReveal = async () => {
    try {
      await axios.post(`${API}/fiscal-dossiers/${dossierId}/audit-nas-reveal`,
        { which: `child_sin_${child.id}` },
        { headers: authH() });
    } catch { /* best-effort */ }
  };

  return (
    <div
      className="border border-slate-200 rounded-lg p-3 bg-amber-50/30"
      data-testid={`fiscal-child-card-${child.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
            {(child.first_name?.[0] || "").toUpperCase()}{(child.last_name?.[0] || "").toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-800 text-sm" data-testid={`fiscal-child-${child.id}-name`}>
              {child.first_name} {child.last_name}
            </div>
            <div className="text-[11px] text-slate-500 flex gap-3 mt-0.5 flex-wrap">
              {child.date_of_birth && (
                <span className="inline-flex items-center gap-0.5">
                  <User className="w-3 h-3" /> DDN: <span className="font-mono">{child.date_of_birth}</span>
                </span>
              )}
              {child.sin && (
                <span className="inline-flex items-center gap-1 font-mono" data-testid={`fiscal-child-${child.id}-sin`}>
                  NAS: {revealed ? child.sin : maskNas(child.sin)}
                  <button
                    onClick={() => {
                      const next = !revealed;
                      setRevealed(next);
                      if (next) auditReveal();
                    }}
                    className="ml-1 text-slate-400 hover:text-slate-700"
                    data-testid={`fiscal-child-${child.id}-sin-toggle`}
                    title={revealed ? "Masquer" : "Révéler (audité)"}
                  >
                    {revealed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </button>
                </span>
              )}
            </div>
            {child.notes && (
              <div className="text-[10px] text-slate-500 italic mt-1">{child.notes}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onEdit}
            disabled={busy}
            className="p-1.5 rounded hover:bg-blue-50 text-slate-500 hover:text-blue-600 transition"
            title="Modifier"
            data-testid={`fiscal-child-${child.id}-edit`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            disabled={busy}
            className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600 transition"
            title="Supprimer"
            data-testid={`fiscal-child-${child.id}-delete`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ChildEditorModal({ child, onClose, onSave, busy }) {
  const isNew = !child;
  const [form, setForm] = useState({
    first_name: child?.first_name || "",
    last_name: child?.last_name || "",
    date_of_birth: child?.date_of_birth || "",
    sin: child?.sin || "",
    notes: child?.notes || "",
  });
  const [revealSin, setRevealSin] = useState(false);

  const submit = () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error("Prénom et nom requis");
      return;
    }
    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      date_of_birth: form.date_of_birth || null,
      sin: form.sin.trim() || null,
      notes: form.notes.trim() || null,
    };
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="fiscal-child-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold inline-flex items-center gap-2">
            <Baby className="w-4 h-4 text-amber-600" />
            {isNew ? "Ajouter un enfant" : "Modifier l'enfant"}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="fiscal-child-modal-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-slate-500 font-medium">Prénom *</span>
              <input
                className="w-full mt-0.5 px-3 py-2 border rounded-lg"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                maxLength={100}
                data-testid="fiscal-child-modal-first-name"
                autoFocus
              />
            </label>
            <label className="block">
              <span className="text-xs text-slate-500 font-medium">Nom *</span>
              <input
                className="w-full mt-0.5 px-3 py-2 border rounded-lg"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                maxLength={100}
                data-testid="fiscal-child-modal-last-name"
              />
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-slate-500 font-medium">Date de naissance</span>
            <input
              type="date"
              className="w-full mt-0.5 px-3 py-2 border rounded-lg font-mono"
              value={form.date_of_birth}
              onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
              data-testid="fiscal-child-modal-dob"
            />
          </label>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-500 font-medium">NAS de l'enfant</span>
              <button
                type="button"
                onClick={() => setRevealSin(!revealSin)}
                className="text-[10px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
              >
                {revealSin ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {revealSin ? "Masquer" : "Révéler"}
              </button>
            </div>
            <input
              type={revealSin ? "text" : "password"}
              className="w-full px-3 py-2 border rounded-lg font-mono"
              value={form.sin}
              onChange={(e) => setForm({ ...form, sin: e.target.value })}
              maxLength={20}
              placeholder="XXX-XXX-XXX (optionnel)"
              data-testid="fiscal-child-modal-sin"
            />
          </div>
          <label className="block">
            <span className="text-xs text-slate-500 font-medium">Notes</span>
            <textarea
              rows={2}
              className="w-full mt-0.5 px-3 py-2 border rounded-lg"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              maxLength={500}
              data-testid="fiscal-child-modal-notes"
            />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
          <button
            onClick={submit}
            disabled={busy || !form.first_name.trim() || !form.last_name.trim()}
            className="px-4 py-1.5 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="fiscal-child-modal-save"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {isNew ? "Ajouter" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmArchive({ onClose, onConfirm }) {
  const [txt, setTxt] = useState("");
  const ok = txt.trim().toUpperCase() === "ARCHIVER";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="fiscal-archive-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800">Archiver ce dossier fiscal ?</h3>
            <p className="text-xs text-slate-500 mt-1">
              Le dossier est mis en archive (récupérable depuis la corbeille). La fiche Master n'est PAS affectée.
            </p>
          </div>
        </div>
        <label className="block text-xs text-slate-700 mt-3">
          Tapez <b className="font-mono text-rose-600">ARCHIVER</b> pour confirmer :
          <input
            value={txt}
            onChange={(e) => setTxt(e.target.value)}
            autoFocus
            className="w-full mt-1 px-3 py-2 border rounded-lg text-sm font-mono uppercase"
            data-testid="fiscal-archive-confirm-input"
          />
        </label>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border" data-testid="fiscal-archive-cancel">
            <X className="w-3.5 h-3.5 inline" /> Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={!ok}
            className="px-4 py-1.5 text-sm rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-30 inline-flex items-center gap-1.5"
            data-testid="fiscal-archive-confirm-btn"
          >
            <Trash2 className="w-3.5 h-3.5" /> Archiver
          </button>
        </div>
      </div>
    </div>
  );
}
